import { useState, useEffect } from 'react';
import { Share2, ShieldCheck, Clock, XCircle, Eye, AlertTriangle, Copy, CheckCircle, Trash2 } from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { PatientCertificateDoc, DocumentShare } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import InstitutionShareModal from '../components/InstitutionShareModal';
import DocumentAccessLogDrawer from '../components/DocumentAccessLogDrawer';
import Spinner from '../../../components/ui/Spinner';

function fmtDate(d: string) {
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ShareCenter() {
  const [shares, setShares] = useState<DocumentShare[]>([]);
  const [certs, setCerts] = useState<PatientCertificateDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [nhcId, setNhcId] = useState('');
  const [tab, setTab] = useState<'active' | 'expired' | 'revoked'>('active');
  const [shareModal, setShareModal] = useState<{ certId: string; title: string } | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [revoking, setRevoking] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([patientApi.getDocumentShares(), patientApi.getCertificates(), patientApi.getProfile()])
      .then(([s, c, p]) => { if (alive) { setShares(s); setCerts(c); setNhcId(p.nhcId); } })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleRevoke = async (id: string) => {
    setRevoking(id);
    try {
      await patientApi.revokeShare(id);
      setShares(prev => prev.map(s => s.id === id ? { ...s, status: 'REVOKED' } : s));
    } catch {} finally { setRevoking(''); }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 2000);
  };

  const filtered = shares.filter(s => {
    if (tab === 'active') return s.status === 'ACTIVE';
    if (tab === 'expired') return s.status === 'EXPIRED';
    return s.status === 'REVOKED';
  });

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <Share2 className="w-6 h-6 text-blue-600" /> Share Center
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Share certificates & documents with institutions</p>
        </div>
        <div className="flex items-center gap-2">
          {nhcId && <NHCIdBadge nhcId={nhcId} />}
          <button onClick={() => setLogOpen(true)} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 px-2.5 py-1 rounded-lg flex items-center gap-1">
            <Eye className="w-3 h-3" /> Access Log
          </button>
        </div>
      </div>

      {/* Consent warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-xs text-amber-800">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Consent & Verification</p>
          <p>NeoVerify proves document authenticity. Consent controls access to private health data. Sharing a certificate does not expose your full medical records.</p>
        </div>
      </div>

      {/* New Share */}
      <div className="card p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Share a Certificate</h3>
        <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {certs.filter(c => c.isShareable && c.status !== 'REVOKED').map(c => (
            <button key={c.id} onClick={() => setShareModal({ certId: c.id, title: c.title })}
              className="text-left p-3 rounded-lg border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-colors">
              <p className="text-xs font-semibold text-gray-900 truncate">{c.title}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{c.certificateType.replace(/_/g, ' ')}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(['active', 'expired', 'revoked'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs font-medium px-4 py-1.5 rounded-md transition-colors capitalize ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {t} ({shares.filter(s => s.status === t.toUpperCase()).length})
          </button>
        ))}
      </div>

      {/* Share list */}
      {filtered.length === 0 ? (
        <div className="text-center py-8">
          <Share2 className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No {tab} shares</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => (
            <div key={s.id} className={`card p-4 ${s.status !== 'ACTIVE' ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{s.sharedWithName}</p>
                  <p className="text-xs text-gray-500">{s.sharedWithRole.replace(/_/g, ' ')}{s.sharedWithInstitution ? ` — ${s.sharedWithInstitution}` : ''}</p>
                  {s.certificate && (
                    <p className="text-xs text-teal-600 mt-1">{s.certificate.title}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.status === 'ACTIVE' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>}
                  {s.status === 'EXPIRED' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500">Expired</span>}
                  {s.status === 'REVOKED' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">Revoked</span>}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-gray-500">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Code: {s.shareCode.substring(0, 8)}...</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Until: {fmtDate(s.validUntil)}</span>
                <span>Accessed: {s.accessCount}x</span>
              </div>

              {s.status === 'ACTIVE' && (
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
                  <button onClick={() => copyCode(s.shareCode)}
                    className="text-[11px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                    {copied === s.shareCode ? <><CheckCircle className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Code</>}
                  </button>
                  <button onClick={() => handleRevoke(s.id)} disabled={revoking === s.id}
                    className="text-[11px] text-red-500 hover:text-red-600 font-medium flex items-center gap-1 ml-auto">
                    {revoking === s.id ? <Spinner size="sm" /> : <><Trash2 className="w-3 h-3" /> Revoke</>}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p style={GOV_COMPLIANCE.text}>Shares are consent grants recorded on your NeoHealthCard account — revocable anytime</p>

      {shareModal && (
        <InstitutionShareModal
          open={!!shareModal}
          onClose={() => { setShareModal(null); patientApi.getDocumentShares().then(setShares); }}
          certificateId={shareModal.certId}
          certificateTitle={shareModal.title}
        />
      )}

      <DocumentAccessLogDrawer open={logOpen} onClose={() => setLogOpen(false)} />
    </div>
  );
}
